import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { utils, writeFile } from "xlsx";

interface DocumentosPendientesChartProps {
  barChartData: any[];
  handleViewBranch: (branchId: number) => void;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={styles.customTooltip}>
        <p style={styles.tooltipLabel}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color, margin: "4px 0" }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DocumentosPendientesChart: React.FC<DocumentosPendientesChartProps> = ({
  barChartData,
  handleViewBranch,
}) => {
  const exportPendingDocuments = () => {
    const ws = utils.aoa_to_sheet([]);

    const headers = [
      "Sucursal",
      "Documentos Pendientes",
      "Documentos Subidos",
      "Total Colaboradores",
    ];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;
    barChartData.forEach((item) => {
      const row = [
        item.fullName,
        item["Documentos Pendientes"],
        item["Documentos Subidos"],
        item["Total Chilitos"],
      ];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    });

    ws["!cols"] = [{ wch: 35 }, { wch: 25 }, { wch: 25 }, { wch: 25 }];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Documentos Pendientes");
    writeFile(wb, "Documentos_Pendientes_por_Sucursal.xlsx");
  };

  return (
    <div style={{ ...styles.chartCard, display: "flex", flexDirection: "column" }}>
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-chart-column"
              style={styles.chartTitleIcon}
            ></i>
            Documentos Pendientes por Sucursal
          </h3>
          <button
            onClick={exportPendingDocuments}
            style={styles.exportChartButton}
            title="Descargar Excel"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#16a34a";
              e.currentTarget.style.borderColor = "#16a34a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#22c55e";
              e.currentTarget.style.borderColor = "#22c55e";
            }}
          >
            <i
              className="fa-solid fa-file-arrow-down"
              style={styles.exportChartIcon}
            ></i>
            <span style={styles.exportChartText}>Excel</span>
          </button>
        </div>
      </div>
      <div style={{ ...styles.chartBody, flex: 1, minHeight: 0, padding: "0 12px 12px 12px" }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
          <ComposedChart
            data={barChartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 60 }}
          >
            <defs>
              <linearGradient id="colorPendientes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorSubidos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend iconType="circle" />
            <Area
              type="monotone"
              dataKey="Documentos Pendientes"
              stroke="#f59e0b"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorPendientes)"
              name="Documentos Pendientes"
              dot={{ fill: "#f59e0b", r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
            <Area
              type="monotone"
              dataKey="Documentos Subidos"
              stroke="#22c55e"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorSubidos)"
              name="Documentos Subidos"
              dot={{ fill: "#22c55e", r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  chartTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    color: "#fff",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  exportChartText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  },
  chartBody: {
    padding: "16px",
    minHeight: "300px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
};

export default DocumentosPendientesChart;

